/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action, computed } from '@ember/object';
import DS from 'ember-data';
import { inject as service } from '@ember/service';
import { isNone } from '@ember/utils';
import { task } from 'ember-concurrency';
import BaseEditorComponent from 'cio/components/condition-list/edit/base';
import NewsletterModel from 'cio/models/newsletter';

type FormattedNewsletter = {
  label: string;
  value: string;
  icon: 'icon-newsletter';
};

function filterFormatNewsletters(
  ns: NewsletterModel[],
  excludedNewsletterConditionId: string | null,
): FormattedNewsletter[] {
  return ns
    .filter((n) => String(n.id) !== String(excludedNewsletterConditionId))
    .map((n) => ({ label: n.name, value: n.id, icon: 'icon-newsletter' }));
}

export default class NewsletterRecipients extends BaseEditorComponent {
  @service store!: DS.Store;

  // external API
  excludedNewsletterConditionId = null;
  disabled = false;

  condition: { newsletter_id: string; negate?: boolean } | null = null;

  onApply() {}

  // internal API
  _selectedNewsletter: FormattedNewsletter | null = null;

  _selectedNegate = null;
  _newsletters: NewsletterModel[] = [];
  _newsletterSearchQuery = '';
  _currentNewslettersPage = 0;
  _loadedAllMatchingNewsletters = false;

  @computed('_newsletters.[]')
  get _filteredFormattedNewsletters() {
    if (!this._newsletters?.length) {
      return [];
    }

    return filterFormatNewsletters(this._newsletters, this.excludedNewsletterConditionId);
  }

  init() {
    super.init();
    return this._setupNewslettersState();
  }

  @action async _setupNewslettersState() {
    this.set('_selectedNegate', isNone(this.condition!.negate) ? false : this.condition!.negate);

    // Try to fast-track setting selected newsletter if already in the store
    // prevents any UI load delay when component is rendered with newsletters already loaded before
    let selectedNewsletterID = this.condition!.newsletter_id;
    if (selectedNewsletterID) {
      let selectedNewsletterRaw = this.store.peekRecord('newsletter', String(selectedNewsletterID));
      if (selectedNewsletterRaw) {
        this.set(
          '_selectedNewsletter',
          filterFormatNewsletters([selectedNewsletterRaw], this.excludedNewsletterConditionId)[0],
        );
      }
    }

    // Load main list of newsletters to select from:
    await this._updateNewslettersTask.perform();

    // Set/fetch selected newsletter if it's not already set:
    if (this._selectedNewsletter != null || !selectedNewsletterID) {
      return;
    }

    // Try reading selected newsletter from first page of newsletters received from the API
    let _selectedNewsletter = this._filteredFormattedNewsletters.find(
      (n) => String(n.value) === String(selectedNewsletterID),
    );
    if (_selectedNewsletter) {
      this.set('_selectedNewsletter', _selectedNewsletter);
      return;
    }

    // try fetching selected newsletter by ID if it's not in the first page of results:
    let selectedNewsletterRaw = await this.store.findRecord('newsletter', String(selectedNewsletterID));
    if (selectedNewsletterRaw) {
      this.set(
        '_selectedNewsletter',
        filterFormatNewsletters([selectedNewsletterRaw], this.excludedNewsletterConditionId)[0],
      );
    }
  }

  _updateNewslettersTask = task({ restartable: true }, async (query?: string) => {
    let newsletters = await this.store.query('newsletter', { page: 0, search: query });

    this.setProperties({
      _newsletters: newsletters.toArray(),
      _currentNewslettersPage: 0,
      _newsletterSearchQuery: query,
      _loadedAllMatchingNewsletters:
        (newsletters as any).meta.pagination.total < (newsletters as any).meta.pagination.size,
    });
  });

  _loadNewslettersNextPageTask = task(async () => {
    if (this._loadedAllMatchingNewsletters) {
      return [];
    }

    this.set('_currentNewslettersPage', this._currentNewslettersPage + 1);

    let nextPageOfNewsletters = await this.store.query('newsletter', {
      page: this._currentNewslettersPage,
      search: this._newsletterSearchQuery,
    });

    if (nextPageOfNewsletters.length) {
      this.set('_newsletters', [...this._newsletters, ...nextPageOfNewsletters.toArray()]);
    } else {
      this.set('_loadedAllMatchingNewsletters', true);
    }

    return filterFormatNewsletters(nextPageOfNewsletters.toArray(), this.excludedNewsletterConditionId);
  });

  @action
  async _onApply() {
    if (!this._selectedNewsletter) {
      return;
    }

    // @ts-ignore: "onApply" not correctly typed, taken from parent Ember classic component class
    await this.onApply({
      type: 'newsletter_recipients',
      newsletter_id: Number(this._selectedNewsletter!.value),
      negate: this._selectedNegate,
    });
  }
}
